import { useLocalPositions } from "@/Common/hooks/Positions/useLocalPositions";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import { TSelectValue } from "@/Layouts/components/InputComponents/SmartSelector/interfaces";
import { useGridApiContext } from "@mui/x-data-grid-premium";
import { useMemo } from "react";

interface IInputEditPositionsCellProps {
	id: string;
	field: string;
	row?: Record<string, unknown>;
	error?: boolean;
	required?: boolean;
	positionType?: string;
	placeholder?: string;
	onOpenAction?: () => void;
	onCloseAction?: () => void;
}

interface IPositionOption {
	label: string;
	addedOption?: string;
}

const InputEditPositionsCell = (props: IInputEditPositionsCellProps) => {
	const {
		id,
		field,
		row,
		error = false,
		required = false,
		positionType,
		placeholder = "Select positions...",
		onOpenAction,
		onCloseAction,
	} = props;

	const apiRef = useGridApiContext();

	const { positions, isLoading } = useLocalPositions({ positionType });

	const currentPositions = useMemo((): string[] => {
		const editRow = apiRef.current.state.editRows[id];
		const editValue = editRow?.[field]?.value;
		const value = editValue ?? row?.[field] ?? [];
		return Array.isArray(value) ? value : [];
	}, [apiRef, id, row, field]);

	const selectedOptions = useMemo(
		(): IPositionOption[] => currentPositions.map((position: string) => ({ label: position })),
		[currentPositions]
	);

	const allOptions = useMemo((): IPositionOption[] => positions.map((position: string) => ({ label: position })), [positions]);

	const handlePositionsChange = (values: TSelectValue = []) => {
		if (!Array.isArray(values)) return;

		const positionValues = values as IPositionOption[];
		const newPositions = positionValues.map((v) => v.addedOption || v.label);
		const positionsString = newPositions.join(", ");

		apiRef.current.setEditCellValue({
			id,
			field,
			value: newPositions,
			debounceMs: 200,
		});

		apiRef.current.setEditCellValue({
			id,
			field: `${field}Display`,
			value: positionsString,
			debounceMs: 200,
		});
	};

	const hasError = required ? !selectedOptions?.length || error : error;

	return (
		<SmartSelector
			multiple
			showBy="label"
			selectBy="label"
			addable={false}
			tagsVariant="text"
			options={allOptions}
			selected={selectedOptions}
			clearable
			inputProps={{ placeholder }}
			listBoxProps={{
				sx: { maxHeight: "220px" },
			}}
			disabled={isLoading}
			error={hasError}
			loading={isLoading}
			onChangeAction={handlePositionsChange}
			onOpenAction={onOpenAction}
			onCloseAction={onCloseAction}
		/>
	);
};

export default InputEditPositionsCell;
